import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import HeroTunnel from "@/components/HeroTunnel";
import Marquee from "@/components/Marquee";
import { useSite } from "@/context/SiteContext";

const FEATURES = [
  { title: "Complete Brand Blueprint", text: "Name, tagline, mission, LLC steps, domain, fonts, palette — every decision a new business must make, captured in one place." },
  { title: "Built-In AI Copilot", text: "Ask it to draft taglines, suggest hex palettes, plan your LLC checklist, or write your Instagram bio. It reads your organizer and personalizes everything — included with your purchase, no extra fees, ever." },
  { title: "One-Click PDF Blueprint", text: "Your entire organizer — every field, every link, every goal — compiled into a clean, printable business blueprint." },
  { title: "Save & Come Back", text: "One payment, lifetime access. Your progress autosaves to the cloud and waits for you on any device." },
  { title: "Custom & Shopify Website Builds", text: "When you're ready to look expensive online, we hand-craft bespoke custom websites and Shopify storefronts tailored to your brand story." },
  { title: "Social Media Management", text: "Monthly done-for-you content, scheduling, and growth — so your feed never goes quiet while you run the business." },
];

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function Home() {
  const { pages } = useSite();
  return (
    <main data-testid="home-page">
      <HeroTunnel />

      {/* Chapter 01 — Who we are */}
      <section className="vector-grid border-t border-[#F3EAE0]" data-testid="who-we-are">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-28 grid lg:grid-cols-[220px_1fr] gap-10">
          <motion.div {...fadeUp}>
            <span className="f-mono text-[#A8785A] text-sm">CH.01</span>
            <div className="w-10 h-px bg-[#E8DCCB] mt-3" />
          </motion.div>
          <div>
            <motion.p {...fadeUp} className="eyebrow mb-5">Who we are</motion.p>
            <motion.h2 {...fadeUp} className="f-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.08] max-w-3xl">
              The operating canvas for founders who think in <span className="f-serif text-[#A8785A]">ideas</span>, not spreadsheets.
            </motion.h2>
            <motion.p {...fadeUp} className="text-[#8C7562] text-base leading-relaxed max-w-2xl mt-7">
              Business Aligned was built for the moment your business lives in seventeen notes apps, three screenshots,
              and a voice memo. We turn that chaos into a structured, living blueprint — your identity, your legal
              foundation, your brand system, your socials, your money, your next ninety days — and then we help you
              act on it with an AI copilot and done-for-you services when you want backup.
            </motion.p>
          </div>
        </div>
      </section>

      <Marquee />

      {/* Chapter 02 — What we do */}
      <section className="bg-[#FFF3F8]" data-testid="what-we-do">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-28 grid lg:grid-cols-[220px_1fr] gap-10">
          <motion.div {...fadeUp}>
            <span className="f-mono text-[#A8785A] text-sm">CH.02</span>
            <div className="w-10 h-px bg-[#E8DCCB] mt-3" />
          </motion.div>
          <div>
            <motion.p {...fadeUp} className="eyebrow mb-5">What we do</motion.p>
            <motion.h2 {...fadeUp} className="f-display text-3xl sm:text-4xl font-medium tracking-tight mb-14">
              Six ways we move your business forward
            </motion.h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {FEATURES.map((f, i) => (
                <motion.div key={f.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 2) * 0.1 }}
                  className="rounded-2xl border border-[#E8DCCB] bg-[#FFFDF7] p-7 hover:border-[#A8785A]/60 transition-colors group"
                  data-testid={`feature-card-${i}`}>
                  <span className="f-mono text-[11px] text-[#A8785A] block mb-5">0{i + 1}</span>
                  <h3 className="f-display font-medium text-2xl sm:text-[1.7rem] tracking-tight leading-tight mb-3">{f.title}</h3>
                  <p className="text-[15px] text-[#8C7562] leading-relaxed">{f.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Chapter 03 — paths */}
      <section className="vector-grid border-t border-[#F3EAE0]" data-testid="paths">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-28">
          <motion.p {...fadeUp} className="eyebrow mb-5">Choose your path</motion.p>
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            {[
              { to: "/organizer", n: "01", title: "The Organizer", text: "One-time purchase, lifetime access. Fill it in yourself or let the copilot draft it with you.", cta: "See what's inside" },
              { to: "/websites", n: "02", title: "Websites", text: "See four bespoke builds and imagine yours. Hand-crafted, no templates.", cta: "View the work", page: "websites" },
              { to: "/social", n: "03", title: "Social Media", text: "Monthly content, scheduling and growth — priced for businesses just starting out.", cta: "See the plans", page: "social" },
              { to: "/2-0", n: "04", title: "Business Aligned 2.0", text: "One-on-one guidance for owners who want help deciding exactly what their business needs next — and building it.", cta: "Meet 2.0", page: "two_point_oh" },
            ].filter((c) => !c.page || pages[c.page]).map((c, idx) => (
              <motion.div key={c.to} {...fadeUp}
                className="rounded-2xl border border-[#E8DCCB] bg-[#FFFDF7] p-8 flex flex-col hover:border-[#D4A0B4] transition-colors"
                data-testid={`path-card-${c.n}`}>
                <span className="f-mono text-[#A8785A] text-xs mb-6">0{idx + 1}</span>
                <h3 className="f-display text-2xl font-medium tracking-tight mb-3">{c.title}</h3>
                <p className="text-sm text-[#8C7562] leading-relaxed flex-1">{c.text}</p>
                <Link to={c.to} data-testid={`path-cta-${c.n}`}
                  className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-[#A8785A] hover:gap-3 transition-[gap]">
                  {c.cta} <ArrowRight size={15} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
